import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Stack,
  Link,
  Rating,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import { useTitle } from "../../hooks/useTitle";
import { useNavigate } from "react-router-dom";
import {
  useActivateCompanyMutation,
  useDisableCompanyMutation,
  useGetCompaniesQuery,
} from "../../services/company.service";
import { Company } from "../../types/company";
import CompanyDrawer from "./CompanyDrawer";
import { formatMoney, isManage360 } from "../../utils/helper";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";
import { Download as DownloadIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import useDebounce from "../../hooks/useDebounce";


const ClientsWorkz360Page = () => {
  useTitle("Clients - Workz360");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const _isManage360 = React.useMemo(() => isManage360(), []);

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState(null);

  const { data, isLoading, isFetching } = useGetCompaniesQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    type: "Workz360",
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doActivate] = useActivateCompanyMutation();
  const [doDisable] = useDisableCompanyMutation();

  const handleUpdateStatusRow = async (
    row: GridRowModel<Company>,
    newStatus: string
  ) => {
    try {
      if (newStatus === "active") {
        await doActivate(row.id).unwrap();
      }
      if (newStatus === "in-active") {
        await doDisable(row.id).unwrap();
      }
      toast.success("Successfully updated");
    } catch (err) { }
  };

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () =>
      [
        {
          field: "customer_name",
          headerName: "Client Name",
          minWidth: isMobile ? 200 : 280,
          flex: 1,
          resizable: true,
          sortingOrder: ["desc", "asc", null],
          filterable: true,
          renderCell: (params: GridRenderCellParams<undefined, Company>) => (
            <Stack direction={"row"} alignItems="center" sx={{ width: "100%" }}>
              <Stack sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction={"row"} alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <Link
                    onClick={() => navigate(`/clients/${params?.row?.id}`)}
                    variant={"body1"}
                    component={"button"}
                    sx={{
                      color: "#F4A259",
                      textDecoration: "none",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {params.row.name}
                  </Link>
                  <Rating
                    value={params.row.total_rating}
                    readOnly
                    size="small"
                  />
                </Stack>
                <Typography
                  variant="caption"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row.primary_contact_name || "Unknown"} -{" "}
                  {params.row.primary_contact_phone_number || "Unknown"}
                </Typography>
              </Stack>
            </Stack>
          ),
          disableExport: true,
        },
        {
          field: "name",
          headerName: "Customer Name",
          hide: true,
        },
        {
          field: "primary_contact_name",
          headerName: "Contact Name",
          hide: true,
        },
        {
          field: "primary_contact_phone_number",
          headerName: "Contact Number",
          hide: true,
        },
        {
          field: "total_rating",
          headerName: "Rating",
          hide: true,
        },
        {
          field: "balance_decrypt",
          headerName: "Balance",
          headerAlign: "center",
          minWidth: isMobile ? 100 : 120,
          resizable: true,
          sortable: false,
          filterable: false,
          align: "center",
          hide: isMobile,
          valueGetter: (params: GridValueGetterParams<undefined, Company>) =>
            `${formatMoney(params.row.balance_decrypt)}`,
        },
        {
          field: "percentage_uplift",
          headerName: "Uplift (%)",
          headerAlign: "center",
          minWidth: isMobile ? 100 : 120,
          resizable: true,
          sortable: false,
          filterable: false,
          align: "center",
          hide: isMobile || _isManage360,
          valueGetter: (params: GridValueGetterParams<undefined, Company>) =>
            !!params.row.percentage_uplift
              ? `${params.row.percentage_uplift}%`
              : `(Not Set)`,
        },
        {
          field: "business_vertical_id",
          headerName: "Industry",
          headerAlign: "center",
          minWidth: isMobile ? 120 : 150,
          resizable: true,
          sortable: true,
          filterable: false,
          align: "center",
          hide: isMobile,
          valueGetter: (params: GridValueGetterParams<undefined, Company>) =>
            `${params.row.business_vertical?.name || "(Not Set)"}`,
        },
        {
          field: "city",
          headerName: "City / State",
          headerAlign: "center",
          minWidth: isMobile ? 120 : 150,
          resizable: true,
          sortable: true,
          filterable: true,
          align: "center",
          hide: isMobile,
          valueGetter: (params: GridValueGetterParams<undefined, Company>) =>
            `${params.row.city}, ${params.row.state}`,
        },
        {
          field: "edit",
          headerName: "Edit",
          headerAlign: "center",
          minWidth: 80,
          resizable: true,
          sortable: true,
          filterable: true,
          align: "center",
          renderCell: (params: GridRenderCellParams<undefined, Company>) => (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/clients/${params?.row?.id}/edit`)}
              sx={{
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  borderColor: "#F4A259",
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                },
              }}
            >
              Edit
            </Button>
          ),
        },
        {
          field: "status",
          headerName: "Status",
          headerAlign: "center",
          minWidth: isMobile ? 100 : 120,
          resizable: true,
          sortable: true,
          filterable: true,
          align: "center",
          renderCell: (params: GridRenderCellParams<undefined, Company>) => (
            <Chip
              label={params.row.status.toUpperCase()}
              size="small"

              color={params.row.status == "active" ? "success" : undefined}
              sx={{ fontSize: 12 }}
            />
          ),
        },
        {
          field: "control",
          headerName: "Toggle",
          headerAlign: "center",
          minWidth: isMobile ? 100 : 120,
          resizable: true,
          sortable: false,
          filterable: false,
          align: "center",
          renderCell: (params: GridRenderCellParams<undefined, Company>) => {
            if (params.row.status === "pending") {
              return (
                <Button
                  variant="contained"
                  size="small"
                  color="info"
                  onClick={() => handleUpdateStatusRow(params.row, "active")}
                >
                  Approve
                </Button>
              );
            } else if (params.row.status === "active") {
              return (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => handleUpdateStatusRow(params.row, "in-active")}
                >
                  Disable
                </Button>
              );
            } else if (params.row.status === "in-active") {
              return (
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => handleUpdateStatusRow(params.row, "active")}
                >
                  Enable
                </Button>
              );
            } else {
              return null;
            }
          },
          disableExport: true,
        },
      ].filter((it) => {
        if (_isManage360) {
          return ![
            "total_rating",
            "percentage_uplift",
            "fixed_price_fee",
            "balance_decrypt",
          ].includes(it.field);
        } else return true;
      }) as GridColDef[],
    [_isManage360, isMobile]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  const onView = (item: any) => {
    setCurrentItem(item);
    setOpen(true);
  };

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns
    const visibleColumns = columns.filter((col) => !col.hide);

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.valueGetter) {
            const params = { row } as any;
            const result = col.valueGetter(params);
            value = result != null ? String(result) : "";
          } else if (col.renderCell) {
            // For renderCell, try to get raw value
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          } else {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Clients_Workz360_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  return (
    <>
      <CardBase
        title="Clients - Workz360"
        useGlassCard={false}
        sx={{ height: isMobile ? "70vh" : "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{ minWidth: isMobile ? "100%" : 250 }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                width: isMobile ? "100%" : "auto",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              {!!query && (
                <Button
                  variant="text"
                  onClick={() => setQuery("")}
                  size="small"
                  sx={{
                    color: "#F4A259",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={!rows || rows.length === 0}
                size="small"
                sx={{
                  minWidth: isMobile ? "auto" : 140,
                  whiteSpace: "nowrap",
                  borderColor: "#F4A259",
                  color: "#F4A259",
                  "&:hover": {
                    borderColor: "#F4A259",
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                  "&:disabled": {
                    borderColor: "rgba(244, 162, 89, 0.3)",
                    color: "rgba(244, 162, 89, 0.3)",
                  },
                }}
              >
                {isMobile ? "Export" : "Export CSV"}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(70vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            width: "100%",
            "& .MuiDataGrid-root": {
              width: "100%",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: 3,
              paddingRight: 3,
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                position: "relative",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
                "&:hover .MuiDataGrid-columnSeparator": {
                  opacity: "1 !important",
                  visibility: "visible !important",
                },
              },
            },
            // Ensure column separator is visible and interactive
            "& .MuiDataGrid-columnSeparator": {
              display: "flex !important",
              visibility: "visible !important",
              opacity: "0.8 !important",
              cursor: "col-resize !important",
              pointerEvents: "auto !important",
              position: "relative",
              zIndex: 100,
              width: "6px !important",
              minWidth: "6px !important",
              "&:hover": {
                opacity: "1 !important",
                color: "#F4A259 !important",
                backgroundColor: "rgba(244, 162, 89, 0.15) !important",
                width: "8px !important",
                minWidth: "8px !important",
              },
              "&.MuiDataGrid-columnSeparator--resizing": {
                opacity: "1 !important",
                color: "#F4A259 !important",
                backgroundColor: "rgba(244, 162, 89, 0.25) !important",
                width: "8px !important",
                minWidth: "8px !important",
              },
              "& button": {
                pointerEvents: "auto !important",
                cursor: "col-resize !important",
                width: "100%",
                height: "100%",
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: 3,
              paddingRight: 3,
              py: 2,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <CompanyDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </>
  );
};
export default ClientsWorkz360Page;
